import { Link } from "@tanstack/react-router";
import {
  BarChart3,
  Boxes,
  ClipboardList,
  Cog,
  FolderCog,
  Home,
  Lock,
  MapPin,
  PanelLeftClose,
  PanelLeftOpen,
  ScrollText,
  Settings2,
  ShieldAlert,
  Users,
  Zap,
} from "lucide-react";
import type { ComponentType } from "react";
import { canManageObra, type MeResponse } from "@/lib/auth";
import { cn } from "@/lib/cn";

// Secciones del menú: los módulos de la obra activa (sin encabezado) y la «Gestión»
// (miembros de la obra + administración global), que sí lo lleva para separarla (F7 #187).
export type NavSectionId = "principal" | "gestion";

// El encabezado visible de cada sección (undefined = sin encabezado, va al principio).
const SECTION_LABEL: Record<NavSectionId, string | undefined> = {
  principal: undefined,
  gestion: "Gestión",
};

const SECTIONS: NavSectionId[] = ["principal", "gestion"];

// NavItem describe una entrada del menú. Los ítems de dominio (F7 #157) cuelgan de la obra
// activa (`/obras/$obraId/...`) y llevan sus `params`; los de gestión global (`/gestion/obras`,
// `/usuarios`) van sin obra. `exact` limita el resaltado activo al índice (Inicio =
// dashboard de la obra), que si no casaría con todas sus subrutas.
export type NavItem = {
  to: string;
  params?: Record<string, string>;
  label: string;
  icon: ComponentType<{ className?: string }>;
  exact?: boolean;
  section: NavSectionId;
};

/**
 * navItemsPara construye el menú para el usuario y la obra activa (F7 #157/#187). Los maestros
 * de fábrica (#28–#31) y la operación LOTO cuelgan de `/obras/$obraId/...`; bajo «Gestión» van
 * los puntos de entrada a la administración que antes quedaban sin enlace: la gestión de
 * miembros de la obra activa (quien puede gestionarla) y la administración global (solo admin:
 * colección de obras y usuarios). El backend es el guardián real (RequireAdmin/
 * RequireObraAdmin) y los guards de ruta redirigen.
 *
 * #252: la obra activa la resuelve useObraActiva (la de la ruta o la recordada), así que el
 * menú es el mismo dentro de la obra y en las rutas globales. Cambiar de obra ya no es una
 * entrada del menú: vive en el conmutador de la cabecera (ObraSwitcher). Sin obra activa la
 * barra solo muestra «Gestión» a quien tenga permiso, y puede quedar vacía.
 */
export function navItemsPara(me: MeResponse | undefined, obraId: number | undefined): NavItem[] {
  const items: NavItem[] = [];

  if (obraId !== undefined) {
    const params = { obraId: String(obraId) };
    items.push(
      {
        to: "/obras/$obraId",
        params,
        label: "Inicio",
        icon: Home,
        exact: true,
        section: "principal",
      },
      {
        to: "/obras/$obraId/lotos",
        params,
        label: "LOTOs",
        icon: ShieldAlert,
        section: "principal",
      },
      { to: "/obras/$obraId/areas", params, label: "Áreas", icon: Boxes, section: "principal" },
      {
        to: "/obras/$obraId/candados",
        params,
        label: "Candados",
        icon: Lock,
        section: "principal",
      },
      { to: "/obras/$obraId/equipos", params, label: "Equipos", icon: Cog, section: "principal" },
      { to: "/obras/$obraId/fuentes", params, label: "Fuentes", icon: Zap, section: "principal" },
      {
        to: "/obras/$obraId/procedimientos",
        params,
        label: "Procedimientos",
        icon: ClipboardList,
        section: "principal",
      },
      { to: "/obras/$obraId/puntos", params, label: "Puntos", icon: MapPin, section: "principal" },
      {
        to: "/obras/$obraId/informes",
        params,
        label: "Informes",
        icon: BarChart3,
        section: "principal",
      },
    );
    // Configuración de la obra activa: alta rápida de miembros con su autorización LOTO y
    // destinatarios de avisos (#238). Es gestión de PERSONAS, así que solo la ven el admin
    // global y el ADMIN_OBRA de la obra (#255 D2).
    if (me && canManageObra(me, obraId)) {
      items.push({
        to: "/obras/$obraId/configuracion",
        params,
        label: "Configuración de obra",
        icon: Settings2,
        section: "gestion",
      });
    }
  }

  if (!me) {
    return items;
  }

  // Administración global (solo admin): colección de obras y usuarios.
  if (me.es_admin) {
    items.push(
      { to: "/gestion/obras", label: "Gestionar obras", icon: FolderCog, section: "gestion" },
      { to: "/usuarios", label: "Usuarios", icon: Users, section: "gestion" },
      // Visor del registro de auditoría (#254): global y ADMIN-only, como los anteriores.
      { to: "/auditoria", label: "Auditoría", icon: ScrollText, section: "gestion" },
    );
  }

  return items;
}

// Objetivos táctiles de 48px: la app se maneja con guantes en planta (ver ui/button.tsx).
const LINK_BASE = "flex min-h-12 items-center rounded-lg transition-colors";
const LINK_ACTIVE = "bg-primary-100 font-medium text-primary-700";
const LINK_INACTIVE = "text-navy-700 hover:bg-primary-50 hover:text-primary-600";

type SidebarProps = {
  items: NavItem[];
  collapsed: boolean;
  toggleCollapsed: () => void;
  open: boolean;
  setOpen: (open: boolean) => void;
};

/**
 * Sidebar es la navegación entre módulos. En escritorio ocupa el centro de la barra lateral
 * de AppLayout y se pliega a solo iconos (la preferencia la persiste useSidebar); en móvil es
 * un drawer sobre un overlay, porque a ese ancho una columna fija se comería la página. Ambas
 * variantes se renderizan siempre y el breakpoint decide cuál se ve.
 */
export function Sidebar({ items, collapsed, toggleCollapsed, open, setOpen }: SidebarProps) {
  // `collapsed` solo aplica en escritorio: el drawer de móvil siempre muestra las etiquetas.
  const renderLink = (iconsOnly: boolean, { to, params, label, icon: Icon, exact }: NavItem) => (
    <Link
      key={to}
      to={to}
      params={params}
      activeOptions={{ exact: exact ?? false }}
      className={cn(LINK_BASE, iconsOnly ? "justify-center px-0" : "gap-3 px-4")}
      activeProps={{ className: LINK_ACTIVE }}
      inactiveProps={{ className: LINK_INACTIVE }}
      // Plegada, el icono es la única pista visible: el título lo explica al puntero y
      // el aria-label al lector de pantalla (el title solo no basta para este último).
      title={iconsOnly ? label : undefined}
      aria-label={iconsOnly ? label : undefined}
    >
      <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
      {!iconsOnly && <span>{label}</span>}
    </Link>
  );

  const renderNav = (iconsOnly: boolean) => (
    <nav aria-label="Navegación principal" className="flex flex-col gap-4 p-4">
      {SECTIONS.map((sectionId) => {
        const deSeccion = items.filter((item) => item.section === sectionId);
        if (deSeccion.length === 0) {
          return null;
        }
        const label = SECTION_LABEL[sectionId];
        return (
          <div key={sectionId} className="flex flex-col gap-2">
            {label ? (
              // Plegada no hay sitio para el rótulo: una línea separa las secciones; expandida,
              // un encabezado tenue en mayúsculas (el rótulo sigue siendo texto para el lector).
              iconsOnly ? (
                <hr className="mx-2 border-slate-200" />
              ) : (
                <p className="px-4 pt-1 font-semibold text-slate-400 text-xs uppercase tracking-wider">
                  {label}
                </p>
              )
            ) : null}
            {deSeccion.map((item) => renderLink(iconsOnly, item))}
          </div>
        );
      })}
    </nav>
  );

  return (
    <>
      <div className="order-4 hidden min-h-0 w-full flex-1 overflow-y-auto border-slate-200 border-r bg-white md:order-none md:block">
        <div className="flex justify-end px-3 pt-3">
          <button
            type="button"
            onClick={toggleCollapsed}
            className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"
            aria-label={collapsed ? "Expandir el menú" : "Plegar el menú"}
            aria-expanded={!collapsed}
          >
            {collapsed ? (
              <PanelLeftOpen className="h-5 w-5" aria-hidden="true" />
            ) : (
              <PanelLeftClose className="h-5 w-5" aria-hidden="true" />
            )}
          </button>
        </div>
        {renderNav(collapsed)}
      </div>

      <button
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        onClick={() => setOpen(false)}
        className={cn(
          "fixed top-26 right-0 bottom-0 left-0 z-40 bg-black/50 transition-opacity md:hidden",
          open ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      />

      <aside
        // Cerrado, el drawer sigue en el DOM (para la transición) pero fuera de la pantalla;
        // `inert` lo saca del foco y del árbol de accesibilidad mientras tanto.
        inert={!open}
        className={cn(
          "fixed top-26 bottom-0 left-0 z-50 w-64 overflow-y-auto bg-white transition-transform md:hidden",
          open ? "translate-x-0" : "-translate-x-full",
        )}
      >
        {renderNav(false)}
      </aside>
    </>
  );
}
